import { Download } from 'lucide-react'
import { useVoucher } from '@/modules/benefits/hooks/useVoucher'
import { useVoucherRedeem } from '@/modules/benefits/hooks/useVoucherRedeem'
import { useVouchers } from '@/modules/benefits/hooks/useVouchers'
import { VoucherRedeemForm } from '@/modules/benefits/components/VoucherRedeemForm'
import { VouchersTable } from '@/modules/benefits/components/VouchersTable'
import { ErrorState } from '@/shared/components/ui/atoms/ErrorState'
import { Can } from '@/modules/auth/components/Can'
import { Permission } from '@/modules/auth/permissions/permissions'
import { exportToExcel } from '@/shared/utils/exportToExcel'

export function VoucherRedeemPage() {
  const { voucher, clientStatus, isLoading: isSearching, error: searchError, search, clear } = useVoucher()
  const { redeem, isSubmitting: isRedeeming, error: redeemError, success, redeemedVoucher, reset } = useVoucherRedeem()

  const {
    data,
    total,
    isLoading: isLoadingList,
    error: listError,
    filters,
    setPage,
    setLimit,
    applyFilters,
    clearFilters,
    refetch,
  } = useVouchers()

  const handleClear = () => {
    clear()
    reset()
  }

  const handleRedeemSuccess = async (...args: Parameters<typeof redeem>) => {
    const result = await redeem(...args)
    refetch()
    return result
  }

  const handleExport = () => {
    exportToExcel(
      data.map((v) => ({
        voucher_code: v.voucher_code,
        client_name: `${v.client.name} ${v.client.last_name}`,
        client_rut: v.client.rut,
        voucher_type: v.voucher_type,
        status: v.status,
        gift_description: v.gift_description ?? '',
        valid_from: v.valid_from,
        valid_until: v.valid_until,
        redeemed_at: v.redeemed_at ?? '',
        redeemed_by: v.redeemed_by?.name ?? '',
        redeemed_product: v.redeemed_product ?? '',
        redeemed_amount: v.redeemed_amount ?? '',
      })),
      'vales',
      [
        { key: 'voucher_code', label: 'Código' },
        { key: 'client_name', label: 'Cliente' },
        { key: 'client_rut', label: 'RUT' },
        { key: 'voucher_type', label: 'Tipo' },
        { key: 'status', label: 'Estado' },
        { key: 'gift_description', label: 'Descripción' },
        { key: 'valid_from', label: 'Válido desde' },
        { key: 'valid_until', label: 'Válido hasta' },
        { key: 'redeemed_at', label: 'Fecha canje' },
        { key: 'redeemed_by', label: 'Canjeado por' },
        { key: 'redeemed_product', label: 'Producto' },
        { key: 'redeemed_amount', label: 'Monto' },
      ],
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Canje de Vales</h1>

      {/* Redeem form */}
      <VoucherRedeemForm
        voucher={voucher}
        clientStatus={clientStatus}
        isSearching={isSearching}
        searchError={searchError}
        onSearch={search}
        onClear={handleClear}
        onRedeem={handleRedeemSuccess}
        isRedeeming={isRedeeming}
        redeemError={redeemError}
        redeemSuccess={success}
        redeemedVoucher={redeemedVoucher}
      />

      {/* Vouchers list table */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Listado de Vales</h2>
          <Can permission={Permission.VOUCHERS_EXPORT}>
            <button onClick={handleExport} className="btn btn-secondary flex items-center gap-1.5">
              <Download size={16} />
              Exportar
            </button>
          </Can>
        </div>

        {listError && <ErrorState message={listError} onRetry={refetch} />}

        <div className="card">
          <VouchersTable
            data={data}
            total={total}
            page={filters.page}
            limit={filters.limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isLoadingList}
            filters={filters}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
          />
        </div>
      </div>
    </div>
  )
}
